import { useEffect, useState } from "react"

import { ReloadOutlined } from "@ant-design/icons"
import { App, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../../Components/Button"
import { RoutesPaths } from "../../../../../constants/user-routes"
import { paymentProcessorTitle } from "../../../../../Utils/utils"
import {
  saveSection,
  selectActiveBuildingBlocks,
  selectActiveProcessor,
  selectConnectedProcessors,
  selectFinishedProcessors,
  selectIsActivated,
  setActiveProcessor
} from "../redux/configure.slice"
import { commitSection, finishSetup, prefillFromWebsite } from "../redux/configure.thunks"
import {
  getFormStepKeys,
  getPrefillStep,
  getSectionLabel,
  hasPreviewStep,
  isExperimentalContentStep,
  resolveStepFields,
  SECTION_KEYS,
  stepSections
} from "../schema/sections"
import { allSectionCounts } from "../utils/sectionProgress"
import {
  firstIncompleteStep,
  isNextEnabled,
  isStepNextEnabled,
  nextBlockReason
} from "../utils/sectionValidation"
import { setupLockReason } from "../utils/setupLock"
import FinishSuccessModal from "./FinishSuccessModal/FinishSuccessModal"

import styles from "../configure.module.less"

// Readable processor name for the finish success modal.
const procTitle = (slug) =>
  paymentProcessorTitle?.[slug] || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug)

const ConfigureFooter = ({ currentSection, onRequestNext }) => {
  const dispatch = useDispatch()
  const isMobile = useSelector((s) => s.nav.isMobile)
  const buildingBlocks = useSelector(selectActiveBuildingBlocks)
  const lastScrapedSite = useSelector((s) => s.disputesConfigure.ui?.lastScrapedSite)
  // Server-recorded last-scraped URL (survives a hard refresh), so re-clicking Next on
  // an unchanged URL after a reload does NOT re-scrape. The backend keeps it in
  // building_blocks._meta.last_scraped_site (serializers.py preserves the _meta flag),
  // which is hydrated into profile.buildingBlocks.
  const serverLastScrapedSite = useSelector(
    (s) => s.disputesConfigure.profile?.buildingBlocks?._meta?.last_scraped_site
  )
  const isActivated = useSelector(selectIsActivated)
  const activeProcessor = useSelector(selectActiveProcessor)
  const connectedProcessors = useSelector(selectConnectedProcessors)
  const finishedProcessors = useSelector(selectFinishedProcessors)
  const prefillRunning = useSelector((s) => s.disputesConfigure.ui?.prefillRunning)
  const autoSaveStatus = useSelector((s) => s.disputesConfigure.ui?.autoSaveStatus)
  const userInfo = useSelector((s) => s.auth.userInfo)
  // RCV-3-21: with onboarding unfinished (or the account still in demo state) the steps past
  // Integrate only redirect back to it, so Next has nowhere to go and stays disabled.
  const setupLocked = !!setupLockReason(userInfo)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { modal, notification } = App.useApp()
  const [busy, setBusy] = useState(false)
  const [resetting, setResetting] = useState(false)
  // Configure success pop-up: { mode: 'processor' | 'all', name, next, nextSlug } or null.
  const [success, setSuccess] = useState(null)
  // A Save/Next/Finish click records its follow-up here; we run it only once the
  // commit (autoSaveStatus) or prefill (prefillRunning) actually resolves, so a
  // failed save never shows success or navigates away (no silent data loss).
  const [pending, setPending] = useState(null)

  // Navigation runs over the FORM steps only: the Preview & Confirm step carries no
  // fields, so the last form step still owns Finish (which is what marks setup done)
  // and lands on Preview afterwards.
  const sectionKeys = getFormStepKeys()
  const idx = sectionKeys.indexOf(currentSection)
  const prevKey = idx > 0 ? sectionKeys[idx - 1] : null
  const nextKey = idx >= 0 && idx < sectionKeys.length - 1 ? sectionKeys[idx + 1] : null
  const isLast = !nextKey

  // Connect steps (Integrate, Enrichment) have no form answers to reset or stage. you
  // either connect a tool or you don't, so Reset / Save for later make no sense there.
  const isIntegrations = currentSection === "integrations"
  const isConnectStep = currentSection === "integrations" || currentSection === "enrichment"

  // An experimental content step aggregates fields from several buckets; validation,
  // commit-on-leave, and reset operate over that field set rather than a single bucket.
  const isExpStep = isExperimentalContentStep(currentSection)

  const sectionState = buildingBlocks?.[currentSection] ?? {}
  const nextEnabled = isExpStep
    ? isStepNextEnabled(currentSection, buildingBlocks)
    : isNextEnabled(currentSection, sectionState, buildingBlocks)
  // Why Next is blocked, so the message matches what the merchant can actually see:
  // pointing at "highlighted fields" when the step is all-optional and nothing is
  // marked leaves them with no way forward and nothing to act on.
  const blockReason = nextEnabled ? null : nextBlockReason(currentSection, buildingBlocks)
  const blockedTitle =
    blockReason === "untouched"
      ? t("Answer at least one question to continue")
      : t("Fill in the required fields to continue")
  const blockedBody =
    blockReason === "untouched"
      ? t(
          "Nothing on this step is required, but answer at least one question so we know it applies to you."
        )
      : t("Please fix the highlighted fields before moving on.")

  // Commit the current step, targeting its real bucket(s). A plain step key like
  // "business_profile" is not a storage bucket, so commit each section it spans.
  const commitCurrent = () => {
    if (isExpStep)
      stepSections(currentSection).forEach((s) => dispatch(commitSection({ section: s })))
    else dispatch(commitSection({ section: currentSection }))
  }

  // What activation requires: connecting a gateway is NOT enough on its own. The
  // merchant must also finish required_information (business identity) and
  // general_information (business story), the same bar the evidence preview uses
  // (LeftRail canPreview), otherwise there's nothing meaningful to fight a dispute
  // with. The payment processor lives only in the Integrate step, so we require the
  // connected gateway explicitly here (required_information no longer gates on it).
  const gatewayConnected =
    Array.isArray(buildingBlocks?.processors?.connected) &&
    buildingBlocks.processors.connected.length > 0
  const step1Valid = isNextEnabled(
    "required_information",
    buildingBlocks?.required_information || {},
    buildingBlocks
  )
  const step2Valid = isNextEnabled(
    "general_information",
    buildingBlocks?.general_information || {},
    buildingBlocks
  )
  // Preview & Confirm flow: finishing requires a gateway AND every required field on
  // every step, with no playground bypass (an incomplete profile must not reach the
  // activate CTA). Every other flow keeps today's rule.
  const missingStep = firstIncompleteStep(buildingBlocks)
  const canActivate = hasPreviewStep()
    ? gatewayConnected && missingStep == null
    : gatewayConnected && step1Valid && step2Valid

  // Per-processor finish: with several processors connected, Finish completes the ONE
  // the merchant is currently on, then either nudges to the next or celebrates the
  // full setup (see commitAndFinish). A single processor finishes the whole thing.
  const connected = Array.isArray(connectedProcessors) ? connectedProcessors : []
  const multiProcessor = connected.length > 1
  const activeSlug = activeProcessor || connected[0]
  const finished = Array.isArray(finishedProcessors) ? finishedProcessors : []

  // Resolve a pending Save/Next once the backend commit reports back: navigate on
  // success, surface an error and stay put on failure.
  useEffect(() => {
    if (!pending || pending.mode !== "commit") return
    if (autoSaveStatus === "saved") {
      // run() may hand back a follow-up pending (finish -> wait for activation);
      // chain it and keep the button busy until that one resolves too.
      const next = pending.run() || null
      setPending(next)
      setBusy(!!next)
    } else if (autoSaveStatus === "error") {
      notification.error({ message: t("Could not save. Check your connection.") })
      setPending(null)
      setBusy(false)
    }
  }, [autoSaveStatus, pending])

  // Reset resolves on the SAME commit lifecycle as everything else: only show
  // "Section reset" once the cleared section is actually saved, and surface an
  // error (no false success) if that commit fails. No arbitrary timer.
  useEffect(() => {
    if (!pending || pending.mode !== "reset") return
    if (autoSaveStatus === "saved") {
      notification.success({ message: t("Section reset") })
      setPending(null)
      setResetting(false)
    } else if (autoSaveStatus === "error") {
      notification.error({ message: t("Could not save. Check your connection.") })
      setPending(null)
      setResetting(false)
    }
  }, [autoSaveStatus, pending])

  // Step-1 prefill resolves via prefillRunning (it commits required_information
  // itself); advance once it finishes whether or not the scrape found anything.
  useEffect(() => {
    if (!pending || pending.mode !== "prefill") return
    if (!prefillRunning) {
      pending.run()
      setPending(null)
      setBusy(false)
    }
  }, [prefillRunning, pending])

  const handleReset = () => {
    modal.confirm({
      title: t("Reset this section?"),
      content: t("All answers in this section will be cleared. This cannot be undone."),
      okText: t("Reset"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      centered: true,
      onOk: () => {
        setResetting(true)
        const clearVal = (v) => (Array.isArray(v) ? [] : v && typeof v === "object" ? {} : "")
        if (isExpStep) {
          // Clear only THIS step's fields, per bucket. Buckets are shared across
          // experimental steps (e.g. checkout_policies feeds Business Profile and
          // Customer Experience), so wiping the whole bucket would drop another
          // step's answers. Nested keys (policy_urls.x) clear at their head object.
          const byBucket = {}
          resolveStepFields(currentSection).forEach(({ section, key }) => {
            const head = key.split(".")[0]
            const st = buildingBlocks?.[section] ?? {}
            byBucket[section] = byBucket[section] || {}
            byBucket[section][head] = clearVal(st[head])
          })
          Object.keys(byBucket).forEach((section) => {
            dispatch(saveSection({ section, patch: byBucket[section] }))
            dispatch(commitSection({ section }))
          })
        } else {
          const cleared = Object.keys(sectionState).reduce((acc, k) => {
            acc[k] = clearVal(sectionState[k])
            return acc
          }, {})
          // Clear the answers, then commit the cleared section deterministically so
          // the reset lifecycle (spinner + toast) ties to the real save resolution
          // via the autoSaveStatus effect above, not a fixed timer.
          dispatch(saveSection({ section: currentSection, patch: cleared }))
          dispatch(commitSection({ section: currentSection }))
        }
        setPending({ mode: "reset" })
      }
    })
  }

  const handleSaveForLater = () => {
    setBusy(true)
    commitCurrent()
    setPending({
      mode: "commit",
      run: () => {
        notification.success({
          message: t("Saved"),
          description: t("We'll keep your progress. You can come back any time.")
        })
        navigate(RoutesPaths.recover.all)
      }
    })
  }

  // Commit the final section, then finish. Finishing NO LONGER activates Recover:
  // activation is now a deliberate, separate step the merchant takes after finishing,
  // from the setup summary (flow v1) or from Preferences (flow v2), once they've
  // reviewed their data and set their preferences. Finishing just marks the profile
  // complete and drops the merchant on the summary. With several processors connected,
  // each Finish completes the ONE the merchant is on and nudges to the next; the
  // summary appears only once all are done. Shared by the direct-finish and the
  // "finish anyway" paths.
  const commitAndFinish = () => {
    setBusy(true)
    commitCurrent()
    setPending({
      mode: "commit",
      run: () => {
        // Finishing is recorded on the account, not in this browser, so it survives a
        // different device and internal staff can see it. The server refuses while a
        // required answer is missing and answers with the refreshed profile otherwise.
        dispatch(finishSetup({ processor: activeSlug }))
        const remaining = connected.filter((s) => s !== activeSlug && !finished.includes(s))

        if (remaining.length > 0) {
          // Still processors to set up: nudge to the next one, stay in Configure.
          setSuccess({
            mode: "processor",
            name: procTitle(activeSlug),
            next: procTitle(remaining[0]),
            nextSlug: remaining[0]
          })
        } else {
          // Every connected processor is set up (or there was only one): land on the
          // dedicated review-and-activate Summary step (NOT step 1). ConfigurePage
          // renders ConfigureSummary for this route.
          navigate(RoutesPaths.recover.configure_section("summary"))
        }

        return null
      }
    })
  }

  // Success-modal actions.
  const goToNextProcessor = () => {
    if (success?.nextSlug) dispatch(setActiveProcessor(success.nextSlug))
    setSuccess(null)
    navigate(RoutesPaths.recover.configure_section("enrichment"))
  }
  const goToDisputes = () => {
    setSuccess(null)
    navigate(RoutesPaths.recover.all)
  }
  // "Activate with one integration": the merchant skips the remaining processor for
  // now and heads to Preferences (review, then activate). Deliberately does NOT mark
  // the whole setup finished: only THIS processor is done (finishSetup above),
  // so the other integration keeps its own live wizard, then its own summary and its
  // own activation later. Preferences unlock on one finished processor.
  const goToPreferencesEarly = () => {
    setSuccess(null)
    navigate(RoutesPaths.recover.preferences)
  }

  // Last step. If any section still has unanswered questions, confirm before
  // finishing (and reassure them progress is saved + they can come back). If
  // everything is already complete, finish straight away. no nagging.
  const handleFinish = () => {
    const counts = allSectionCounts(buildingBlocks)
    const incomplete = SECTION_KEYS.filter((k) => {
      // Integrate is never part of the "unanswered questions" confirm: it has no form
      // answers, only a connected gateway. Activation already requires that gateway via
      // canActivate (gatewayConnected && required_information + general_information
      // valid) and the Finish button is hard-disabled until canActivate is true, so
      // listing Integrate here is a no-op.
      if (k === "integrations") return false
      const c = counts[k] || { answered: 0, total: 0 }
      return !(c.total > 0 && c.answered === c.total)
    })
    if (incomplete.length === 0) {
      commitAndFinish()
      return
    }
    modal.confirm({
      title: t("Finish setup now?"),
      content: t(
        "Some steps still have unanswered questions. You can finish now and complete them whenever you like; your progress is always saved."
      ),
      okText: t("Finish now"),
      cancelText: t("Keep editing"),
      centered: true,
      onOk: commitAndFinish
    })
  }

  const handleNext = () => {
    if (!nextEnabled) {
      onRequestNext?.(false)
      notification.warning({ message: blockedTitle, description: blockedBody })
      return
    }
    // Final step: route through the finish confirmation instead of advancing.
    if (!nextKey) {
      handleFinish()
      return
    }
    setBusy(true)

    // Step-1 differentiator: read the merchant's website and pre-fill the rest
    // of the wizard. We only scrape when the URL actually changed since the last
    // scrape. We compare against BOTH the client-side ui.lastScrapedSite AND the
    // server-recorded last-scraped URL on the profile: the client value is reset
    // by a hard refresh (the disputes slice is not persisted), so on its own it
    // lets a re-clicked Next re-scrape an unchanged URL and churn the profile (it
    // looks like the form is "answering on its own"). Matching either source keeps
    // the skip honoured across a reload. When we do prefill, that saga commits
    // required_information itself (commit, scrape, refetch, in order), so we don't
    // fire a separate commit here.
    const norm = (u) =>
      (u || "")
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/\/+$/, "")
    const website = buildingBlocks?.required_information?.website_url
    const alreadyScraped =
      !!website &&
      (norm(website) === norm(lastScrapedSite) || norm(website) === norm(serverLastScrapedSite))
    // The website URL lives in the required_information bucket; the scrape fires when
    // leaving whichever step owns it (mode-aware, see getPrefillStep in the schema).
    const prefillStep = getPrefillStep()
    const willPrefill = currentSection === prefillStep && !!website && !alreadyScraped

    if (willPrefill) {
      // The prefill saga commits required_information itself, then REFETCHES the profile
      // and replaces Redux with the server copy. Anything this step holds in OTHER
      // buckets is still only a local draft at that point, so the refetch would wipe it.
      // Experimental steps aggregate several buckets (Business Profile spans
      // required_information + general_information + checkout_policies), so commit the
      // rest FIRST and only start the scrape once that save has actually landed.
      const otherBuckets = isExpStep
        ? stepSections(currentSection).filter((s) => s !== "required_information")
        : []
      const startPrefill = () => {
        dispatch(prefillFromWebsite({ website_url: website }))
        return {
          mode: "prefill",
          run: () => navigate(RoutesPaths.recover.configure_section(nextKey))
        }
      }
      if (otherBuckets.length) {
        otherBuckets.forEach((s) => dispatch(commitSection({ section: s })))
        // The commit effect chains whatever pending this run() returns, so the scrape
        // fires only after autoSaveStatus reports 'saved'.
        setPending({ mode: "commit", run: startPrefill })
      } else {
        setPending(startPrefill())
      }
    } else {
      // nextKey is guaranteed here. the final step finishes via handleFinish above.
      commitCurrent()
      setPending({
        mode: "commit",
        run: () => navigate(RoutesPaths.recover.configure_section(nextKey))
      })
    }
  }

  return (
    <div className={styles.configureFooter}>
      <div className={styles.footerLeft}>
        {!isConnectStep ? (
          <>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              loading={resetting}
              onClick={handleReset}
              text={t("Reset")}
            />
            <Button
              type="default"
              loading={busy}
              onClick={handleSaveForLater}
              text={t("Save for later")}
            />
          </>
        ) : null}
      </div>
      <div className={styles.footerCenter}>
        {/* The Integrate step has no form answers to persist (its commit is a
            client-side no-op), so don't claim "Auto-saved" when leaving it. */}
        {autoSaveStatus && autoSaveStatus !== "idle" && !isIntegrations ? (
          <span className={`${styles.saveStatus} ${styles[autoSaveStatus] ?? ""}`}>
            {autoSaveStatus === "saving"
              ? `${t("Saving")}…`
              : autoSaveStatus === "saved"
                ? t("Auto-saved")
                : t("Could not save. Check your connection.")}
          </span>
        ) : null}
      </div>
      <div className={styles.footerRight}>
        {prevKey ? (
          <Button
            type="default"
            // Locked while a commit/activation is in flight (like the primary and
            // Save-for-later buttons), so going back can't dispatch a second commit
            // that races the pending one.
            disabled={busy}
            onClick={() => {
              commitCurrent()
              navigate(RoutesPaths.recover.configure_section(prevKey))
            }}
            // Mobile drops the step name: the two nav buttons share one phone-width
            // row, so the full label had to be clipped mid-word to fit.
            text={
              isMobile ? `← ${t("Previous")}` : `← ${t("Previous")}: ${t(getSectionLabel(prevKey))}`
            }
          />
        ) : null}
        <Tooltip
          title={
            isLast
              ? !canActivate
                ? !gatewayConnected
                  ? t("Connect a payment gateway to finish setup.")
                  : // Preview & Confirm flow: every required field must be filled, so
                    // name the step that still needs attention.
                    hasPreviewStep()
                    ? t('Complete "_step_" to finish setup.', {
                        step: t(getSectionLabel(missingStep))
                      })
                    : !step1Valid && !step2Valid
                      ? t("Complete steps 1 and 2 to finish setup.")
                      : !step1Valid
                        ? t("Complete step 1 to finish setup.")
                        : t("Complete step 2 to finish setup.")
                : ""
              : !nextEnabled
                ? blockedTitle
                : ""
          }
        >
          <span>
            <Button
              type="primary"
              // On the final step the button finishes setup, so it's hard-disabled
              // until setup can complete: a connected gateway AND steps 1 + 2 done.
              // Activation itself happens later, from the summary or Preferences.
              // On earlier steps it stays clickable when incomplete so handleNext's
              // blocked branch highlights the required fields; only `busy` disables there.
              disabled={busy || setupLocked || (isLast && !canActivate)}
              loading={busy}
              onClick={handleNext}
              text={
                isLast
                  ? multiProcessor
                    ? t("Finish _name_", { name: procTitle(activeSlug) })
                    : isActivated
                      ? t("Finish & save")
                      : t("Finish setup")
                  : isMobile
                    ? `${t("Next")} →`
                    : `${t("Next")}: ${t(getSectionLabel(nextKey))} →`
              }
            />
          </span>
        </Tooltip>
      </div>

      <FinishSuccessModal
        mode={success?.mode || null}
        name={success?.name}
        next={success?.next}
        onContinue={goToNextProcessor}
        onGoToPreferences={goToPreferencesEarly}
        onGoToDisputes={goToDisputes}
      />
    </div>
  )
}

export default ConfigureFooter
